import React from 'react';
import { Paper, Typography, Box, Chip, LinearProgress, Tooltip, Divider, IconButton } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getAchievementColor, formatNumber, formatPercent, getBaseDataDateText, getTransmitCycleBadgeColorByLabel } from './utils';

const CompanySummaryCard = ({ company, availableKPIs, onDetailClick }) => {
    if (!company || !company.subscriptions) {
        return (
            <Paper elevation={1} sx={{ p: 2, height: '200px', backgroundColor: '#f5f5f5' }}>
                <Box sx={{ textAlign: 'center', color: '#999', mt: 4 }}>
                    <Typography variant="body2">데이터 없음</Typography>
                </Box>
            </Paper>
        );
    }

    const activeSubscriptions = company.subscriptions.filter(sub => 
        sub.kpiData && sub.kpiData.length > 0
    );

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5 },
                minHeight: { 
                    xs: '140px',    // 최소 높이만 지정
                    sm: '150px',    
                    md: '160px',    
                    lg: '170px',    
                    xl: '180px'     
                },
                height: 'auto',     // 내용에 따라 자동 조정
                minWidth: 0,
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                },
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
            }}
        >
            {/* 회사 정보 헤더 */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, justifyContent: 'center' }}>
                <Box sx={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    maxWidth: '100%'
                }}>
                    <BusinessIcon sx={{ mr: 0.5, color: '#1976d2', fontSize: 14 }} />
                    <Typography
                        variant="subtitle1"
                        fontWeight="700"
                        sx={{
                            color: '#1976d2',
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        {company.companyName}
                    </Typography>
                </Box>
            </Box>

            {/* KPI 구독 정보 (최대 2개 표시) */}
            <Box sx={{ flex: 1 }}>
                {activeSubscriptions.slice(0, 2).map((subscription, index) => {
                    const latestKpiData = subscription.kpiData[subscription.kpiData.length - 1];
                    const measuredValue = latestKpiData.measuredValue || 0;

                    return (
                        <Box key={subscription.kpiIndicatorCode} sx={{ mb: 1.5 }}>
                            {/* KPI 이름과 전송주기 배지, 성취율 */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                                    <Typography 
                                        variant="body2" 
                                        fontWeight="600"
                                        sx={{ 
                                            color: '#333', 
                                            fontSize: '0.75rem',
                                            lineHeight: '1.2',
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                            overflow: 'visible'
                                        }}
                                    >
                                        {subscription.kpiIndicatorName}
                                    </Typography>
                                    <Chip
                                        icon={<ScheduleIcon sx={{ fontSize: 10 }} />}
                                        label={subscription.transmitCycleLabel}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: '20px',
                                            backgroundColor: getTransmitCycleBadgeColorByLabel(subscription.transmitCycleLabel),
                                            color: 'white',
                                            fontWeight: 'bold',
                                            '& .MuiChip-icon': {
                                                color: 'white',
                                                fontSize: '10px'
                                            }
                                        }}
                                    />
                                </Box>
                                <Typography 
                                    variant="body2" 
                                    fontWeight="bold"
                                    sx={{
                                        color: getAchievementColor((measuredValue / subscription.targetValue) * 100),
                                        fontSize: '0.8rem',
                                        ml: 0.5
                                    }}
                                >
                                    {formatNumber(measuredValue, 2)} {latestKpiData.unit || ''}
                                </Typography>
                            </Box>
                            
                            {/* 진행률 바 */}
                            <LinearProgress 
                                variant="determinate" 
                                value={Math.min((measuredValue / subscription.targetValue) * 100, 100)}
                                sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: '#f0f0f0',
                                    mb: 0.5,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getAchievementColor((measuredValue / subscription.targetValue) * 100),
                                        borderRadius: 2
                                    }
                                }}
                            />
                            
                            {/* 현재값/목표값 */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#666',
                                        fontSize: '0.65rem',
                                        fontWeight: 500
                                    }}
                                >
                                    {formatNumber(Math.floor((latestKpiData.measuredValue || 0) * 100) / 100, 2)} / {formatNumber(Math.floor((subscription.targetValue || 0) * 100) / 100, 2)} {latestKpiData.unit || ''}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#999',
                                        fontSize: '0.55rem',
                                        fontStyle: 'italic',
                                        mt: 0.2
                                    }}
                                >
                                    (측정값 / 목표값)
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
                
                {/* 더 많은 KPI가 있을 때 표시 */}
                {activeSubscriptions.length > 2 && (
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: '#666',
                            fontSize: '0.6rem',
                            textAlign: 'center',
                            display: 'block',
                            mt: 0.5
                        }}
                    >
                        외 {activeSubscriptions.length - 2}개 KPI 구독 중
                    </Typography>
                )}
            </Box>

            {/* 상세 보기 버튼 */}
            <Box sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8,
                zIndex: 1 
            }}>
                <Tooltip title="상세 그래프 보기">
                    <IconButton 
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDetailClick?.(company.companyCode, company.companyName);
                        }}
                        sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            color: '#1976d2',
                            width: 28,
                            height: 28,
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                                transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
};

export default CompanySummaryCard;